import { useEffect, useState } from "react";

import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { Appearance, loadStripe, Stripe } from "@stripe/stripe-js";
import { SnugStripe } from "src/sections/dashboard/stripe/stripe";
import { PromoCodeField } from "src/sections/dashboard/stripe/promo-code-field";
import { StripePaymentIntent } from "src/types/stripe";

export interface StripeElementProps {
  ud_id: string | null;
  handlePaymentSuccess: (payment_method_id?: string) => void;
  paymentRequired: boolean;
  handleCreateOrUpdatePaymentIntent?: (
    ud_id: string | null,
    paymentIntentId: string | null,
    product: string,
    paymentScope?: "INDIVIDUAL" | "HOUSEHOLD"
  ) => void;
  currentPaymentIntent: StripePaymentIntent | null;
  willOrTrust: string;
  amount: number;
  onCancel?: () => void;
  paymentScope?: "INDIVIDUAL" | "HOUSEHOLD";
  // Lets a checkout with its own order summary show the promo discount there
  onPaymentIntentChange?: (paymentIntent: StripePaymentIntent) => void;
}

export function StripeElement(props: StripeElementProps) {
  const {
    ud_id,
    handlePaymentSuccess,
    handleCreateOrUpdatePaymentIntent,
    paymentRequired,
    currentPaymentIntent,
    willOrTrust,
    onCancel,
    paymentScope,
    onPaymentIntentChange,
  } = props;

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  // The intent as updated by a promo code, until the parent hands down a new one
  const [promoIntent, setPromoIntent] = useState<StripePaymentIntent | null>(null);
  useEffect(() => setPromoIntent(null), [currentPaymentIntent]);
  const paymentIntent = promoIntent?.id === currentPaymentIntent?.id ? promoIntent : currentPaymentIntent;

  useEffect(() => {
    // Only create payment intent if one doesn't exist yet to prevent infinite loop
    if (paymentRequired && !currentPaymentIntent?.id && handleCreateOrUpdatePaymentIntent) {
      handleCreateOrUpdatePaymentIntent(
        ud_id,
        currentPaymentIntent?.id || null,
        willOrTrust,
        paymentScope
      );
    }
  }, [
    ud_id,
    paymentRequired,
    handleCreateOrUpdatePaymentIntent,
    currentPaymentIntent?.id,
    willOrTrust,
    paymentScope,
  ]);

  // Initialize Stripe with the publishable key and connected account if available
  useEffect(() => {
    // Get Stripe publishable key from environment variable
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    if (currentPaymentIntent && currentPaymentIntent?.metadata?.stripe_connect_account_id) {
      // Load Stripe with the connected account
      const promise = loadStripe(publishableKey, {
        stripeAccount: currentPaymentIntent?.metadata?.stripe_connect_account_id,
      });
      setStripePromise(promise);
    } else if (currentPaymentIntent) {
      // Load Stripe without a connected account (direct Snug account)
      const promise = loadStripe(publishableKey);
      setStripePromise(promise);
    }

    // Reset when payment intent changes
    return () => {
      setStripePromise(null);
    };
  }, [currentPaymentIntent]);

  // Linear-inspired appearance - clean, minimal, modern
  const appearance = {
    theme: 'flat',
    variables: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      fontSizeBase: '14px',
      fontLineHeight: '1.5',
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightBold: '600',
      borderRadius: '8px',
      colorPrimary: '#5E6AD2',
      colorBackground: '#ffffff',
      colorText: '#1a1a1a',
      colorTextSecondary: '#6b6f76',
      colorTextPlaceholder: '#9ca3af',
      colorDanger: '#e5484d',
      colorSuccess: '#30a46c',
      spacingUnit: '4px',
      spacingGridRow: '16px',
      spacingGridColumn: '16px',
    },
    rules: {
      '.Block': {
        backgroundColor: '#ffffff',
        boxShadow: 'none',
        padding: '0',
      },
      '.Input': {
        backgroundColor: '#ffffff',
        border: '1px solid #e1e4e8',
        borderRadius: '8px',
        padding: '10px 12px',
        fontSize: '14px',
        boxShadow: 'none',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      },
      '.Input:hover': {
        border: '1px solid #c9ccd1',
      },
      '.Input:focus': {
        border: '1px solid #5E6AD2',
        boxShadow: '0 0 0 3px rgba(94, 106, 210, 0.1)',
        outline: 'none',
      },
      '.Input--invalid': {
        border: '1px solid #e5484d',
        boxShadow: 'none',
      },
      '.Input--invalid:focus': {
        border: '1px solid #e5484d',
        boxShadow: '0 0 0 3px rgba(229, 72, 77, 0.1)',
      },
      '.Input:disabled, .Input--invalid:disabled': {
        backgroundColor: '#f6f8fa',
        color: '#9ca3af',
        border: '1px solid #e1e4e8',
      },
      '.Label': {
        fontSize: '13px',
        fontWeight: '500',
        color: '#1a1a1a',
        marginBottom: '6px',
      },
      // Accordion layout styles (for payment method options)
      '.AccordionItem': {
        border: '1px solid #e7e7e7',
        borderRadius: '8px',
        marginBottom: '8px',
      },
      '.AccordionItem--selected': {
        border: '1px solid #5E6AD2',
      },
      // Tab layout styles (fallback if tabs layout is used)
      '.Tab': {
        backgroundColor: '#ffffff',
        border: '1px solid #e7e7e7',
        borderRadius: '4px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#6b6f76',
        transition: 'all 0.15s ease',
      },
      '.Tab:hover': {
        backgroundColor: '#f6f8fa',
        color: '#1a1a1a',
        border: '1px solid #e7e7e7',
      },
      '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
        backgroundColor: '#ffffff',
        border: '1px solid #5E6AD2',
        color: '#1a1a1a',
        boxShadow: 'none',
      },
      '.TabIcon': {
        fill: '#6b6f76',
      },
      '.TabIcon--selected': {
        fill: '#5E6AD2',
      },
      '.Error': {
        fontSize: '13px',
        color: '#e5484d',
        marginTop: '6px',
      },
      '.CheckboxInput': {
        backgroundColor: '#ffffff',
        border: '1px solid #e1e4e8',
        borderRadius: '4px',
      },
      '.CheckboxInput--checked': {
        backgroundColor: '#5E6AD2',
        borderColor: '#5E6AD2',
      },
      '.CheckboxLabel': {
        fontSize: '13px',
        color: '#1a1a1a',
      },
    },
  } as Appearance;

  return (
    <>
      {currentPaymentIntent?.id && stripePromise ? (
        <Elements
          key={currentPaymentIntent.id}
          stripe={stripePromise}
          options={{
            clientSecret: currentPaymentIntent.client_secret,
            loader: "auto",
            appearance: appearance,
          }}
        >
          {paymentRequired && paymentIntent && (
            <PromoCodeField
              ud_id={ud_id}
              paymentIntent={paymentIntent}
              product={willOrTrust}
              paymentScope={paymentScope}
              onPaymentIntentChange={(intent) => {
                setPromoIntent(intent);
                onPaymentIntentChange?.(intent);
              }}
              showTotals={!onPaymentIntentChange}
            />
          )}
          <SnugStripe
            key="stripes"
            handlePaymentSuccess={handlePaymentSuccess}
            paymentRequired={paymentRequired}
            currentPaymentIntent={paymentIntent}
            onCancel={onCancel}
          />
        </Elements>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            py: 4
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Preparing payment form...
            </Typography>
          </Stack>
        </Box>
      )}
    </>
  );
}
